import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { callFunction, db } from '../firebase';
import { expandGambiaPhone } from '../policyCatalog';
import { TENANT_COMPANY_NAME, TENANT_ID, isPublicBrandSlug } from '../tenant';
import { readClientCreds, saveClientCreds } from '../clientSession';
import PulseLogo from '../components/PulseLogo';
import PulseItGroupFooter from '../components/PulseItGroupFooter';
import { useI18n, usePublicTenantLocale } from '../i18n';
import { moduleOn, useDirectoryFeatures } from '../tenantFeatures';
import '../styles/attestation.css';
import '../styles/client-portal.css';

function ClientLogin() {
    const { slug } = useParams();
    const tenantSlug = TENANT_ID;
    const { t } = useI18n();
    usePublicTenantLocale(tenantSlug);
    const features = useDirectoryFeatures(tenantSlug);
    const portalOn = moduleOn(features, 'clientPortal');
    const navigate = useNavigate();
    const [phone, setPhone] = useState('');
    const [pin, setPin] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [company, setCompany] = useState({ companyName: TENANT_COMPANY_NAME, logoUrl: '' });

    useEffect(() => {
        if (readClientCreds()) navigate('/client', { replace: true });
        getDoc(doc(db, 'directory', TENANT_ID)).then((snap) => {
            if (snap.exists()) {
                const row = snap.data();
                setCompany({ companyName: TENANT_COMPANY_NAME, logoUrl: row.logoUrl || '' });
            }
        }).catch(() => {});
    }, [navigate, tenantSlug]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const data = await callFunction('lookupMyPolicy', {
                phone: expandGambiaPhone(phone),
                pin,
                tenantSlug
            });
            if (!data.results || !data.results.length) {
                setError(t('clientLogin.noPolicy'));
                setLoading(false);
                return;
            }
            saveClientCreds({ phone: expandGambiaPhone(phone), pin, tenantSlug });
            navigate('/client');
        } catch (err) {
            setError(err.message || 'Could not sign in with that phone and PIN.');
            setLoading(false);
        }
    };

    return (
        <div className="att-shell">
            <div className="row justify-content-center g-0">
                <div className="col-12 col-md-7 col-lg-5 col-xl-4">
                    <div className="card shadow-lg border-0 rounded-3 att-lookup-card">
                        <div className="card-body">
                            <div className="text-center mb-3">
                                <PulseLogo size="small" showTagline={false} companyName={company.companyName} logoUrl={company.logoUrl} />
                            </div>
                            <h5 className="text-center mb-1">{t('clientLogin.title')}</h5>
                            <p className="text-center text-muted small mb-4">
                                {portalOn ? t('clientLogin.blurb') : t('clientLogin.disabled')}
                            </p>
                            {error && <div className="alert alert-danger">{error}</div>}
                            {portalOn ? (
                                <form onSubmit={handleLogin}>
                                    <div className="mb-3">
                                        <label className="form-label">{t('clientLogin.phone')}</label>
                                        <input className="form-control" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="700001001 or +220 700001001" autoComplete="tel" required />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">{t('clientLogin.pin')}</label>
                                        <input className="form-control" inputMode="numeric" maxLength="6" value={pin} onChange={(e) => setPin(e.target.value)} placeholder={t('clientLogin.pinPlaceholder')} autoComplete="current-password" required />
                                    </div>
                                    <button className="btn btn-success w-100" disabled={loading}>{loading ? t('clientLogin.signingIn') : t('common.signIn')}</button>
                                </form>
                            ) : null}
                            {portalOn && <p className="text-center text-muted small mt-3 mb-0">{t('clientLogin.policeHint')}</p>}
                            <div className="text-center mt-3">
                                <Link to={slug && isPublicBrandSlug(slug) ? '/c/' + String(slug).trim().toLowerCase() : '/login'} className="small">{t('clientLogin.staffLogin')}</Link>
                            </div>
                        </div>
                    </div>
                    <PulseItGroupFooter variant="dark" />
                </div>
            </div>
        </div>
    );
}

export default ClientLogin;
